import { create } from 'zustand';
import {
  adGenerationApi,
  generationApi,
  type AdPackJob,
  type AdPackStatusResponse,
} from '@/services/api';
import {
  subscribeToGenerationRuns,
  subscribeToGenerationRun,
  findRunIdByJobId,
} from '@/services/firestoreListeners';
import type { Unsubscribe } from 'firebase/firestore';

interface AdGenerationState {
  jobs: AdPackJob[];
  total: number;
  selectedJob: AdPackStatusResponse | null;
  loading: boolean;
  error: string | null;

  // Archive state
  showArchived: boolean;
  archiving: string | null; // job_id being archived/unarchived

  // Real-time subscription state
  isRealtime: boolean;
  realtimeError: string | null;

  // REST API methods (fallback)
  fetchJobs: () => Promise<void>;
  fetchJobStatus: (jobId: string) => Promise<void>;
  cancelJob: (jobId: string) => Promise<void>;
  clearSelection: () => void;

  // Archive methods
  setShowArchived: (show: boolean) => void;
  archiveJob: (job: AdPackJob) => Promise<void>;
  unarchiveJob: (job: AdPackJob) => Promise<void>;

  // Real-time subscription methods
  subscribeToJobs: () => void;
  subscribeToJob: (jobId: string) => Promise<void>;
  unsubscribeAll: () => void;
}

// Store subscription cleanup functions
let jobsUnsubscribe: Unsubscribe | null = null;
let jobDetailUnsubscribe: Unsubscribe | null = null;

export const useAdGenerationStore = create<AdGenerationState>((set, get) => ({
  jobs: [],
  total: 0,
  selectedJob: null,
  loading: false,
  error: null,
  showArchived: false,
  archiving: null,
  isRealtime: false,
  realtimeError: null,

  // ---------------------------------------------------------------------------
  // REST API methods (fallback when Firestore unavailable)
  // ---------------------------------------------------------------------------

  fetchJobs: async () => {
    set({ loading: true, error: null });
    try {
      const data = await adGenerationApi.listJobs(50);
      set({ jobs: data.jobs, total: data.total });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to load ad generation jobs' });
    } finally {
      set({ loading: false });
    }
  },

  fetchJobStatus: async (jobId: string) => {
    set({ loading: true, error: null });
    try {
      const status = await adGenerationApi.getJobStatus(jobId);
      set({ selectedJob: status });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to load job status' });
    } finally {
      set({ loading: false });
    }
  },

  cancelJob: async (jobId: string) => {
    try {
      await adGenerationApi.cancelJob(jobId);
      set((s) => ({
        jobs: s.jobs.map((j) =>
          j.job_id === jobId ? { ...j, status: 'cancelled' } : j,
        ),
      }));
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to cancel job' });
    }
  },

  clearSelection: () => {
    // Unsubscribe from job detail if subscribed
    if (jobDetailUnsubscribe) {
      jobDetailUnsubscribe();
      jobDetailUnsubscribe = null;
    }
    set({ selectedJob: null });
  },

  // ---------------------------------------------------------------------------
  // Real-time subscription methods
  // ---------------------------------------------------------------------------

  setShowArchived: (show: boolean) => {
    set({ showArchived: show });
    // Re-subscribe with new filter settings
    get().subscribeToJobs();
  },

  archiveJob: async (job: AdPackJob) => {
    // Use run_id if available (from Firestore), otherwise find it
    let runId: string | null | undefined = job.run_id;
    if (!runId) {
      runId = await findRunIdByJobId(job.job_id);
    }
    if (!runId) {
      set({ error: `Could not find run ID for job ${job.job_id}` });
      return;
    }

    set({ archiving: job.job_id, error: null });
    try {
      await generationApi.archiveRun(runId);
      // Refresh the list
      get().subscribeToJobs();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to archive job' });
    } finally {
      set({ archiving: null });
    }
  },

  unarchiveJob: async (job: AdPackJob) => {
    // Use run_id if available (from Firestore), otherwise find it
    let runId: string | null | undefined = job.run_id;
    if (!runId) {
      runId = await findRunIdByJobId(job.job_id);
    }
    if (!runId) {
      set({ error: `Could not find run ID for job ${job.job_id}` });
      return;
    }

    set({ archiving: job.job_id, error: null });
    try {
      await generationApi.unarchiveRun(runId);
      // Refresh the list
      get().subscribeToJobs();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to unarchive job' });
    } finally {
      set({ archiving: null });
    }
  },

  subscribeToJobs: () => {
    // Cleanup existing subscription
    if (jobsUnsubscribe) {
      jobsUnsubscribe();
      jobsUnsubscribe = null;
    }

    // In dev bypass mode the mock user has no real Firebase token — skip Firestore
    // and go straight to REST to avoid noisy permission-denied errors.
    if (import.meta.env.VITE_DEV_AUTH_BYPASS === 'true') {
      set({ isRealtime: false, loading: false, realtimeError: null });
      get().fetchJobs();
      return;
    }

    const { showArchived } = get();
    set({ loading: true, error: null, realtimeError: null });

    try {
      jobsUnsubscribe = subscribeToGenerationRuns(
        // onUpdate
        (jobs, total) => {
          set({
            jobs,
            total,
            loading: false,
            isRealtime: true,
            realtimeError: null,
          });
        },
        // onError - fallback to REST API
        (error) => {
          console.warn('[AdGeneration] Firestore subscription failed, falling back to REST:', error);
          set({
            isRealtime: false,
            realtimeError: error.message,
            loading: false,
          });
          // Fallback to REST API
          get().fetchJobs();
        },
        // Options
        { includeArchived: showArchived },
      );
    } catch (error) {
      console.warn('[AdGeneration] Failed to subscribe to Firestore:', error);
      set({
        isRealtime: false,
        realtimeError: error instanceof Error ? error.message : 'Subscription failed',
        loading: false,
      });
      // Fallback to REST API
      get().fetchJobs();
    }
  },

  subscribeToJob: async (jobId: string) => {
    // Cleanup existing subscription
    if (jobDetailUnsubscribe) {
      jobDetailUnsubscribe();
      jobDetailUnsubscribe = null;
    }

    // In dev bypass mode, skip Firestore and use REST directly.
    if (import.meta.env.VITE_DEV_AUTH_BYPASS === 'true') {
      await get().fetchJobStatus(jobId);
      return;
    }

    set({ loading: true, error: null });

    try {
      // First, find the Firestore run_id for this job_id
      const runId = await findRunIdByJobId(jobId);

      if (!runId) {
        console.log(`[AdGeneration] Run not found for job ${jobId}, using REST API`);
        // Fallback to REST API
        await get().fetchJobStatus(jobId);
        return;
      }

      jobDetailUnsubscribe = subscribeToGenerationRun(
        runId,
        // onUpdate
        (status) => {
          if (status) {
            set({ selectedJob: status, loading: false });
          } else {
            // Document not found, fallback to REST
            get().fetchJobStatus(jobId);
          }
        },
        // onError - fallback to REST API
        (error) => {
          console.warn('[AdGeneration] Job subscription failed, falling back to REST:', error);
          set({ loading: false });
          get().fetchJobStatus(jobId);
        },
      );
    } catch (error) {
      console.warn('[AdGeneration] Failed to subscribe to job:', error);
      set({ loading: false });
      // Fallback to REST API
      await get().fetchJobStatus(jobId);
    }
  },

  unsubscribeAll: () => {
    if (jobsUnsubscribe) {
      jobsUnsubscribe();
      jobsUnsubscribe = null;
    }
    if (jobDetailUnsubscribe) {
      jobDetailUnsubscribe();
      jobDetailUnsubscribe = null;
    }
    set({ isRealtime: false });
  },
}));
